import React, { useState, useEffect, useCallback } from 'react';
import { Audit } from '@/firebase/auditService';
import { auditReadinessService, AuditReadiness } from '@/services/auditReadinessService';
import { useNotification } from '@/contexts/NotificationContext';
import { CheckCircle, AlertCircle, Clock, FileText, XCircle, TrendingUp } from 'lucide-react';

interface AuditReadinessDashboardProps {
  audit: Audit;
  onRefresh?: () => void;
  showActions?: boolean; // Whether to show action buttons
  onReadinessUpdate?: (readiness: AuditReadiness) => void; // Callback when readiness is calculated
}

const AuditReadinessDashboard: React.FC<AuditReadinessDashboardProps> = ({ 
  audit, 
  onRefresh,
  showActions = true,
  onReadinessUpdate
}) => {
  const { showNotification } = useNotification();
  const [readiness, setReadiness] = useState<AuditReadiness | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateReadiness = useCallback(async () => {
    if (!audit.id) return;

    setIsLoading(true);
    try {
      const result = await auditReadinessService.calculateReadiness(audit);
      setReadiness(result);
      // Notify parent component of readiness update
      if (onReadinessUpdate) {
        onReadinessUpdate(result);
      }
    } catch (error: any) {
      console.error('Error calculating readiness:', error);
      showNotification(`Error calculating readiness: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [audit, showNotification]);

  useEffect(() => {
    if (audit.id) {
      calculateReadiness();
    }
  }, [audit.id, audit.locationId, audit.complianceTemplateIds, audit.associatedTaskIds, calculateReadiness]);

  // Real-time updates: Recalculate when tasks/evidence might have changed
  // This is a simple approach - in production, you might want to use Firestore listeners
  useEffect(() => {
    if (!audit.id) return;
    
    // Recalculate readiness every 30 seconds for active audits
    if (audit.status === 'planned' || audit.status === 'in_progress') {
      const interval = setInterval(() => {
        calculateReadiness();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [audit.id, audit.status, calculateReadiness]);

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 90) return 'bg-green-600';
    if (score >= 70) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const getScoreBarColor = (score: number): string => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Calculating readiness...</span>
        </div>
      </div>
    );
  }

  if (!readiness) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Unable to calculate readiness</p>
          <button
            onClick={calculateReadiness}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasMissingItems = 
    readiness.missingTasks.length > 0 ||
    readiness.missingEvidenceTypes.length > 0 ||
    readiness.missingPrerequisites.length > 0;

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      {/* Overall Score */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-800">Audit Readiness</h3>
          <span className={`text-2xl font-bold ${getScoreColor(readiness.overallScore)}`}>
            {readiness.overallScore}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${getScoreBarColor(readiness.overallScore)}`}
            style={{ width: `${readiness.overallScore}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {readiness.overallScore >= 90 
            ? 'Ready to start audit' 
            : readiness.overallScore >= 70 
            ? 'Almost ready - complete missing items' 
            : 'Not ready - significant items missing'}
        </p>
      </div>

      {/* Breakdown */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Requirements Breakdown</h4>
        <div className="space-y-3">
          {/* Task Completion */}
          <div className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Task Completion</span>
              </div>
              <span className={`text-sm font-semibold ${getScoreColor(readiness.taskCompletion.score)}`}>
                {readiness.taskCompletion.score}%
              </span>
            </div>
            <div className="text-xs text-gray-600">
              {readiness.taskCompletion.completedTasks} of {readiness.taskCompletion.totalTasks} tasks completed
              {readiness.taskCompletion.overdueTasks > 0 && (
                <span className="text-red-600 ml-2">
                  ({readiness.taskCompletion.overdueTasks} overdue)
                </span>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${getScoreBarColor(readiness.taskCompletion.score)}`}
                style={{ width: `${readiness.taskCompletion.score}%` }}
              ></div>
            </div>
          </div>

          {/* Evidence Availability */}
          <div className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Evidence Availability</span>
              </div>
              <span className={`text-sm font-semibold ${getScoreColor(readiness.evidenceAvailability.score)}`}>
                {readiness.evidenceAvailability.score}%
              </span>
            </div>
            <div className="text-xs text-gray-600">
              {readiness.evidenceAvailability.availableEvidenceTypes} of {readiness.evidenceAvailability.requiredEvidenceTypes} evidence types available
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${getScoreBarColor(readiness.evidenceAvailability.score)}`}
                style={{ width: `${readiness.evidenceAvailability.score}%` }}
              ></div>
            </div>
          </div>

          {/* Prerequisite Audits */}
          {readiness.prerequisiteAudits.required && (
            <div className="border border-gray-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Prerequisite Audits</span>
                </div>
                <span className={`text-sm font-semibold ${getScoreColor(readiness.prerequisiteAudits.score)}`}>
                  {readiness.prerequisiteAudits.score}%
                </span>
              </div>
              <div className="text-xs text-gray-600">
                {readiness.prerequisiteAudits.completed ? 'All prerequisites completed' : 'Prerequisites pending'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gap Analysis */}
      {hasMissingItems && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Missing Items</h4>
          <div className="space-y-3">
            {/* Missing Tasks */}
            {readiness.missingTasks.length > 0 && (
              <div className="border border-orange-200 rounded-md p-3 bg-orange-50">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    Missing Tasks ({readiness.missingTasks.length})
                  </span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {readiness.missingTasks.map((task, idx) => (
                    <div key={idx} className="text-xs text-gray-700 flex items-center justify-between">
                      <span>{task.taskName}</span>
                      {task.isOverdue && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs ml-2">
                          Overdue
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {showActions && (
                  <button
                    onClick={() => {
                      // Navigate to tasks filtered by this location
                      window.location.href = `/tasks?location=${audit.locationId}`;
                    }}
                    className="mt-2 text-xs text-orange-700 hover:text-orange-900 underline"
                  >
                    View Tasks →
                  </button>
                )}
              </div>
            )}

            {/* Missing Evidence */}
            {readiness.missingEvidenceTypes.length > 0 && (
              <div className="border border-orange-200 rounded-md p-3 bg-orange-50">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    Missing Evidence ({readiness.missingEvidenceTypes.length})
                  </span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {readiness.missingEvidenceTypes.map((evidence, idx) => (
                    <div key={idx} className="text-xs text-gray-700">
                      <span className="font-medium">{evidence.templateName}:</span> {evidence.evidenceLabel}
                      {evidence.required && (
                        <span className="text-red-600 ml-1">*</span>
                      )}
                    </div>
                  ))}
                </div>
                {showActions && (
                  <button
                    onClick={() => {
                      // Navigate to evidence vault filtered by this location
                      window.location.href = `/evidence-vault?location=${audit.locationId}`;
                    }}
                    className="mt-2 text-xs text-orange-700 hover:text-orange-900 underline"
                  >
                    View Evidence Vault →
                  </button>
                )}
              </div>
            )}

            {/* Missing Prerequisites */}
            {readiness.missingPrerequisites.length > 0 && (
              <div className="border border-orange-200 rounded-md p-3 bg-orange-50">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    Missing Prerequisites ({readiness.missingPrerequisites.length})
                  </span>
                </div>
                <div className="text-xs text-gray-700">
                  Previous audits must be completed before starting this audit.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Missing Items */}
      {!hasMissingItems && readiness.overallScore === 100 && (
        <div className="border border-green-200 rounded-md p-4 bg-green-50">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              All requirements met! Ready to start audit.
            </span>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={calculateReadiness}
          className="text-xs text-gray-600 hover:text-gray-800 underline"
          disabled={isLoading}
        >
          {isLoading ? 'Recalculating...' : 'Refresh Readiness'}
        </button>
      </div>
    </div>
  );
};

export default AuditReadinessDashboard;

